import { Link } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { GET_MY_STORE, type ManageMyStoreData } from '../../graphql/management'
import { useAuth } from '../../auth/AuthContext'
import { canManageMall as roleCanManageMall } from '../../auth/roles'
import { StoreStatusBadge } from './StoreStatusBadge'

// Seller/KOL home: store header + stats + quick actions + read-only list of the
// products assigned to this store. Mirrors iOS MyStoreView.
export default function MyStore() {
  const { user } = useAuth()
  const manageMall = user?.canManageMall ?? roleCanManageMall(user?.role)
  const { data, loading, error } = useQuery<ManageMyStoreData>(GET_MY_STORE, { fetchPolicy: 'cache-and-network' })

  if (loading && !data) return <div className="muted">Loading your store…</div>
  if (error) return <p className="error">Couldn’t load your store: {error.message}</p>

  const store = data?.myStore
  if (!store) {
    return (
      <div className="empty-state">
        <h2>No store yet</h2>
        <p className="muted">You don’t have a store assigned to your account.</p>
      </div>
    )
  }

  const first = user?.fullName?.split(' ')[0]
  return (
    <div className="manage-page">
      <header className="manage-header">
        <div>
          <h1>{store.name}</h1>
          {first && <p className="muted">Welcome back, {first}.</p>}
        </div>
        <StoreStatusBadge status={store.status} />
      </header>

      <div className="manage-grid">
        <div className="stat-card"><span className="stat-value">{store.productsCount}</span><span className="stat-label">Products</span></div>
        <div className="stat-card"><span className="stat-value">{store.activeProductsCount}</span><span className="stat-label">Active</span></div>
        <div className="stat-card"><span className="stat-value">£{(store.commissionEarned ?? 0).toFixed(2)}</span><span className="stat-label">Commission</span></div>
      </div>

      <div className="manage-actions">
        <Link className="btn-secondary" to="/manage/store/settings">Store settings</Link>
        <Link className="btn-secondary" to="/">Livestreams</Link>
        {manageMall && <Link className="btn-primary" to="/manage/products/new">Add product</Link>}
      </div>

      <section>
        <h2 className="manage-subhead">Products in your store</h2>
        {store.storeProducts.filter((sp) => sp.isActive).length === 0 ? (
          <p className="muted">No products assigned yet.</p>
        ) : (
          <ul className="product-mgmt-list">
            {store.storeProducts.filter((sp) => sp.isActive).map((sp) => (
              <li key={sp.id} className="product-mgmt-row">
                {sp.product.imageUrls?.[0]
                  ? <img src={sp.product.imageUrls[0]} alt="" className="product-mgmt-thumb" />
                  : <div className="product-mgmt-thumb product-mgmt-thumb--empty" />}
                <span className="product-mgmt-title">{sp.product.title}</span>
                <span className="muted small">{sp.product.status}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
